import { LabelColors } from "../../../Packs/PackCard/types";
import { PricingTypes } from "../../../../../enums/licensing/pricingTypes";
import { type ColumnOptions, type LicenseConfirmationItem } from "../../../../../interfaces";
import utils from "../../../../../utils/miscellaneousUtils";
import { TextAlign } from "../../../../../enums/textAlign";

export type LicenseConfirmationFormItem = LicenseConfirmationItem & { licensesCount: number };

export const getLabelOption = (item: LicenseConfirmationFormItem) => {
  if (item.isAccountPackLicenseExpired) {
    return { name: "Expired", color: LabelColors.EXPIRED, tooltip: "Pack renews with this purchase" };
  }

  if (item.packPricingTypeId === PricingTypes.Free) {
    return { name: "Free", color: LabelColors.FREE };
  }

  return undefined;
};

export const calculateTotals = (items: LicenseConfirmationFormItem[]) => {
  return items.reduce(
    (totals, item) => {
      totals.issuingTotal += computeIssuingLicenses(item);
      if (
        (item.packPricingTypeId === PricingTypes.Paid && !item.isTrial) ||
        (item.isAccountPackLicenseExpired && ![PricingTypes.Other, PricingTypes.Free].includes(item.packPricingTypeId))
      ) {
        totals.licensesCount += item.licensesCount;
      }
      return totals;
    },
    { issuingTotal: 0, licensesCount: 0 },
  );
};

export const formatNumber = (value: number): string => {
  return value.toLocaleString("en-US");
};

export const formatUsedLicenses = (item: LicenseConfirmationFormItem): string => {
  const used = formatNumber(item.totalUsedLicenses);
  const total = utils.unlimitedNumber(item.totalLicenses);
  return `${used}/${total}`;
};

export const getColumns = (isPurchaseNeeded: boolean): ColumnOptions[] => {
  const base = [
    { name: "Packs" },
    { name: "Issuing", textAlign: TextAlign.Center },
    { name: "Used", textAlign: TextAlign.Center },
  ];

  return isPurchaseNeeded ? [...base, { name: "Purchasing" }] : base;
};

export const getInitialValues = (items: LicenseConfirmationItem[]) => {
  return items.map((item) => {
    const purchasingLicenses =
      item.isAccountPackLicenseExpired && item.packPricingTypeId === PricingTypes.Paid
        ? item.totalUsedLicenses
        : item.purchasingLicenses;
    return { ...item, licensesCount: purchasingLicenses, purchasingLicenses };
  });
};

export const computeIssuingLicenses = (item: LicenseConfirmationFormItem): number => {
  const { packPricingTypeId, isTrial, isAccountPackLicenseExpired } = item;

  const shouldIssue =
    packPricingTypeId === PricingTypes.Other ||
    (isTrial && !isAccountPackLicenseExpired) ||
    packPricingTypeId === PricingTypes.Free;

  return shouldIssue ? item.issuingLicenses + item.purchasingLicenses : item.issuingLicenses;
};

export const needsLicensePurchase = (item: LicenseConfirmationItem) =>
  [PricingTypes.Paid, PricingTypes.Other].includes(item.packPricingTypeId) &&
  ((item.purchasingLicenses && !item.isTrial) || (item.isAccountPackLicenseExpired && item.totalUsedLicenses));
